import type { ProjectDocumentV3 } from "./projectDocument";
import type { ProjectStore } from "./projectStore";

const DATABASE_NAME = "sped-scheduler-timetable-versions";
const DATABASE_VERSION = 1;
const VERSION_STORE = "timetable_versions";
const HEAD_STORE = "timetable_version_heads";
const JOURNAL_STORE = "timetable_version_journal";

export type TimetableVersionSource =
  | "MIGRATED"
  | "STRICT"
  | "REPAIR"
  | "PARTIAL";
export type TimetableCompleteness = "COMPLETE" | "INCOMPLETE";

export interface VersionApplication {
  application_id: string;
  source: Exclude<TimetableVersionSource, "MIGRATED">;
  completeness: TimetableCompleteness;
  solver_status: string | null;
  session_id?: string;
  round_id?: string;
}

export interface TimetableVersionProvenance {
  actual_repairs: unknown[];
  unassigned_requirements: unknown[];
  blocker_factors: unknown[];
}

export interface TimetableVersionRecord {
  id: string;
  project_id: string;
  label: string;
  date_key: string;
  day_sequence: number;
  source: TimetableVersionSource;
  completeness: TimetableCompleteness;
  scheduled_lessons: number;
  required_lessons: number;
  solver_status: string | null;
  optimality_claim: boolean;
  actual_repairs: unknown[];
  unassigned_requirements: unknown[];
  blocker_factors: unknown[];
  condition_revision: number;
  condition_fingerprint: string | null;
  source_application_id: string;
  created_at: string;
  status: "PREPARED" | "COMMITTED";
  document_snapshot: ProjectDocumentV3;
}

export interface TimetableVersionHead {
  project_id: string;
  version_id: string;
  source_application_id: string;
  document_revision: number;
  document_hash: string;
  updated_at: string;
}

interface VersionJournal {
  operation_id: string;
  project_id: string;
  kind: "APPLICATION" | "ACTIVE_UPDATE";
  status: "PREPARED" | "COMMITTED";
  version_id: string;
  base_revision: number;
  base_hash: string;
  target_revision: number;
  target_hash: string;
  target_version: TimetableVersionRecord;
  created_at: string;
  completed_at: string | null;
}

export class TimetableVersionStorageError extends Error {
  readonly code: string;
  readonly causeValue: unknown;

  constructor(code: string, message: string, causeValue?: unknown) {
    super(message);
    this.name = "TimetableVersionStorageError";
    this.code = code;
    this.causeValue = causeValue;
  }
}

export interface TimetableVersionStore {
  reconcileProject(projectStore: ProjectStore, projectId: string): Promise<void>;
  ensureMigrated(document: ProjectDocumentV3): Promise<TimetableVersionRecord | null>;
  applyApplication(
    projectStore: ProjectStore,
    current: ProjectDocumentV3,
    target: ProjectDocumentV3,
    application: VersionApplication,
    provenance: TimetableVersionProvenance
  ): Promise<TimetableVersionRecord>;
  updateActiveSnapshot(
    projectStore: ProjectStore,
    current: ProjectDocumentV3,
    target: ProjectDocumentV3
  ): Promise<void>;
  list(projectId: string): Promise<TimetableVersionRecord[]>;
  get(projectId: string, versionId: string): Promise<TimetableVersionRecord | null>;
  head(projectId: string): Promise<TimetableVersionHead | null>;
  close(): void;
}

export async function openTimetableVersionStore(): Promise<TimetableVersionStore> {
  if (!("indexedDB" in globalThis)) {
    throw new TimetableVersionStorageError(
      "VERSION_STORAGE_UNAVAILABLE",
      "当前浏览器不支持课表版本存储"
    );
  }
  if (!globalThis.navigator?.locks) {
    throw new TimetableVersionStorageError(
      "VERSION_STORAGE_UNAVAILABLE",
      "当前浏览器不支持安全的课表版本保存，请使用最新版 Chrome 或 Edge"
    );
  }
  try {
    return new IndexedDbTimetableVersionStore(await openDatabase());
  } catch (error) {
    throw new TimetableVersionStorageError(
      "VERSION_STORAGE_OPEN_FAILED",
      "无法打开课表版本存储，现有课表没有被改动",
      error
    );
  }
}

class IndexedDbTimetableVersionStore implements TimetableVersionStore {
  constructor(private readonly database: IDBDatabase) {
    database.addEventListener("versionchange", () => database.close());
  }

  async reconcileProject(projectStore: ProjectStore, projectId: string): Promise<void> {
    await this.withProjectLock(projectId, () =>
      this.reconcileProjectUnlocked(projectStore, projectId)
    );
  }

  private async reconcileProjectUnlocked(
    projectStore: ProjectStore,
    projectId: string
  ): Promise<void> {
    const journals = await this.preparedJournals(projectId);
    for (const journal of journals) {
      const current = await projectStore.get(projectId);
      if (!current) {
        throw new TimetableVersionStorageError(
          "VERSION_RECOVERY_CONFLICT",
          "课表版本恢复失败：当前项目不存在"
        );
      }
      const currentHash = await documentHash(current);
      if (
        current.document_revision === journal.target_revision &&
        currentHash === journal.target_hash
      ) {
        await this.commitJournal(journal.operation_id);
        continue;
      }
      if (
        current.document_revision === journal.base_revision &&
        currentHash === journal.base_hash
      ) {
        await this.rollbackJournal(journal.operation_id);
        continue;
      }
      throw new TimetableVersionStorageError(
        "VERSION_RECOVERY_CONFLICT",
        "检测到未完成的课表版本保存，当前数据与保存前后均不一致，请先刷新页面"
      );
    }
  }

  async ensureMigrated(
    document: ProjectDocumentV3
  ): Promise<TimetableVersionRecord | null> {
    return this.withProjectLock(document.id, () => this.ensureMigratedUnlocked(document));
  }

  private async ensureMigratedUnlocked(
    document: ProjectDocumentV3
  ): Promise<TimetableVersionRecord | null> {
    const existingHead = await this.head(document.id);
    if (existingHead) {
      const existing = await this.get(document.id, existingHead.version_id);
      if (!existing) {
        throw new TimetableVersionStorageError(
          "VERSION_HEAD_INVALID",
          "当前课表版本记录缺失，原有课表没有被改动"
        );
      }
      return existing;
    }
    if (document.entities.timetable_lessons.length === 0) return null;
    const now = new Date().toISOString();
    const hash = await documentHash(document);
    const applicationId = `migrated:${document.id}:${hash}`;
    const transaction = this.database.transaction(
      [VERSION_STORE, HEAD_STORE, JOURNAL_STORE],
      "readwrite"
    );
    try {
      const versions = transaction.objectStore(VERSION_STORE);
      const heads = transaction.objectStore(HEAD_STORE);
      const currentHead = await requestResult<TimetableVersionHead | undefined>(
        heads.get(document.id)
      );
      if (currentHead) {
        await transactionDone(transaction);
        return this.get(document.id, currentHead.version_id);
      }
      const record = await this.makeRecord(
        versions,
        document,
        applicationId,
        {
          application_id: applicationId,
          source: "STRICT",
          completeness: "COMPLETE",
          solver_status: null
        },
        { actual_repairs: [], unassigned_requirements: [], blocker_factors: [] },
        now,
        "MIGRATED"
      );
      record.status = "COMMITTED";
      versions.put(structuredClone(record));
      heads.put({
        project_id: document.id,
        version_id: record.id,
        source_application_id: applicationId,
        document_revision: document.document_revision,
        document_hash: hash,
        updated_at: now
      } satisfies TimetableVersionHead);
      await transactionDone(transaction);
      return record;
    } catch (error) {
      abortQuietly(transaction);
      if (isConstraintError(error)) {
        const head = await this.head(document.id);
        return head ? this.get(document.id, head.version_id) : null;
      }
      throw versionWriteError(error);
    }
  }

  async applyApplication(
    projectStore: ProjectStore,
    current: ProjectDocumentV3,
    target: ProjectDocumentV3,
    application: VersionApplication,
    provenance: TimetableVersionProvenance
  ): Promise<TimetableVersionRecord> {
    return this.withProjectLock(current.id, async () => {
      await this.reconcileProjectUnlocked(projectStore, current.id);
      const existing = await this.byApplication(application.application_id);
      if (existing?.status === "COMMITTED") {
        if (
          (await documentHash(existing.document_snapshot)) !==
          (await documentHash(target))
        ) {
          throw new TimetableVersionStorageError(
            "VERSION_APPLICATION_CONFLICT",
            "服务器返回的课表与已保存版本不一致"
          );
        }
        return existing;
      }
      if (target.document_revision !== current.document_revision + 1) {
        throw new TimetableVersionStorageError(
          "VERSION_APPLICATION_STALE",
          "课表版本应用已失效，请刷新后重试"
        );
      }
      const journal = await this.prepareApplication(
        current,
        target,
        application,
        provenance
      );
      try {
        await projectStore.putIfRevision(target, current.document_revision);
      } catch (error) {
        await this.rollbackJournal(journal.operation_id);
        throw error;
      }
      try {
        await this.commitJournal(journal.operation_id);
      } catch (error) {
        throw new TimetableVersionStorageError(
          "VERSION_COMMIT_PENDING",
          "课表已保存，版本记录将在刷新后自动完成，请勿重复排课",
          error
        );
      }
      const record = await this.get(current.id, journal.version_id);
      if (!record) {
        throw new TimetableVersionStorageError(
          "VERSION_COMMIT_PENDING",
          "课表已保存，版本记录将在刷新后自动完成，请刷新页面"
        );
      }
      return record;
    });
  }

  async updateActiveSnapshot(
    projectStore: ProjectStore,
    current: ProjectDocumentV3,
    target: ProjectDocumentV3
  ): Promise<void> {
    await this.withProjectLock(current.id, async () => {
      await this.reconcileProjectUnlocked(projectStore, current.id);
      const head = await this.head(current.id);
      if (!head) {
        await projectStore.putIfRevision(target, current.document_revision);
        return;
      }
      const record = await this.get(current.id, head.version_id);
      if (!record) {
        throw new TimetableVersionStorageError(
          "VERSION_HEAD_INVALID",
          "当前课表版本记录缺失，原有课表没有被改动"
        );
      }
      const now = new Date().toISOString();
      const targetVersion: TimetableVersionRecord = {
        ...record,
        scheduled_lessons: target.entities.timetable_lessons.length,
        document_snapshot: structuredClone(target),
        status: "COMMITTED"
      };
      const journal = await this.prepareJournal(
        current,
        target,
        targetVersion,
        "ACTIVE_UPDATE",
        now
      );
      try {
        await projectStore.putIfRevision(target, current.document_revision);
      } catch (error) {
        await this.rollbackJournal(journal.operation_id);
        throw error;
      }
      try {
        await this.commitJournal(journal.operation_id);
      } catch (error) {
        throw new TimetableVersionStorageError(
          "VERSION_COMMIT_PENDING",
          "人工调课已保存，版本记录将在刷新后自动完成",
          error
        );
      }
    });
  }

  async list(projectId: string): Promise<TimetableVersionRecord[]> {
    const transaction = this.database.transaction(VERSION_STORE, "readonly");
    const index = transaction.objectStore(VERSION_STORE).index("project_id");
    const values = await requestResult<TimetableVersionRecord[]>(index.getAll(projectId));
    await transactionDone(transaction);
    return values
      .filter((value) => value.status === "COMMITTED")
      .sort(
        (left, right) =>
          right.date_key.localeCompare(left.date_key) ||
          right.day_sequence - left.day_sequence ||
          right.created_at.localeCompare(left.created_at)
      )
      .map((value) => structuredClone(value));
  }

  async get(
    projectId: string,
    versionId: string
  ): Promise<TimetableVersionRecord | null> {
    const transaction = this.database.transaction(VERSION_STORE, "readonly");
    const value = await requestResult<TimetableVersionRecord | undefined>(
      transaction.objectStore(VERSION_STORE).get(versionId)
    );
    await transactionDone(transaction);
    return value?.project_id === projectId && value.status === "COMMITTED"
      ? structuredClone(value)
      : null;
  }

  async head(projectId: string): Promise<TimetableVersionHead | null> {
    const transaction = this.database.transaction(HEAD_STORE, "readonly");
    const value = await requestResult<TimetableVersionHead | undefined>(
      transaction.objectStore(HEAD_STORE).get(projectId)
    );
    await transactionDone(transaction);
    return value ? structuredClone(value) : null;
  }

  close(): void {
    this.database.close();
  }

  private async withProjectLock<T>(
    projectId: string,
    callback: () => Promise<T>
  ): Promise<T> {
    return globalThis.navigator.locks.request(
      `sped-scheduler-timetable-version:${projectId}`,
      callback
    );
  }

  private async prepareApplication(
    current: ProjectDocumentV3,
    target: ProjectDocumentV3,
    application: VersionApplication,
    provenance: TimetableVersionProvenance
  ): Promise<VersionJournal> {
    const now = new Date().toISOString();
    const baseHash = await documentHash(current);
    const targetHash = await documentHash(target);
    const transaction = this.database.transaction(
      [VERSION_STORE, JOURNAL_STORE],
      "readwrite"
    );
    try {
      const versions = transaction.objectStore(VERSION_STORE);
      const existing = await requestResult<TimetableVersionRecord | undefined>(
        versions.index("source_application_id").get(application.application_id)
      );
      if (existing?.status === "COMMITTED") {
        abortQuietly(transaction);
        return {
          operation_id: existing.source_application_id,
          project_id: existing.project_id,
          kind: "APPLICATION",
          status: "COMMITTED",
          version_id: existing.id,
          base_revision: current.document_revision,
          base_hash: baseHash,
          target_revision: target.document_revision,
          target_hash: targetHash,
          target_version: existing,
          created_at: existing.created_at,
          completed_at: existing.created_at
        };
      }
      await this.assertNoPreparedJournal(
        transaction.objectStore(JOURNAL_STORE),
        current.id
      );
      const record = await this.makeRecord(
        versions,
        target,
        application.application_id,
        application,
        provenance,
        now
      );
      record.status = "PREPARED";
      versions.add(structuredClone(record));
      const journal = this.journalValue(
        current,
        target,
        record,
        "APPLICATION",
        now,
        baseHash,
        targetHash
      );
      transaction.objectStore(JOURNAL_STORE).add(structuredClone(journal));
      await transactionDone(transaction);
      return journal;
    } catch (error) {
      abortQuietly(transaction);
      throw versionWriteError(error);
    }
  }

  private async prepareJournal(
    current: ProjectDocumentV3,
    target: ProjectDocumentV3,
    targetVersion: TimetableVersionRecord,
    kind: VersionJournal["kind"],
    now: string
  ): Promise<VersionJournal> {
    const journal = this.journalValue(
      current,
      target,
      targetVersion,
      kind,
      now,
      await documentHash(current),
      await documentHash(target)
    );
    const transaction = this.database.transaction(JOURNAL_STORE, "readwrite");
    try {
      const store = transaction.objectStore(JOURNAL_STORE);
      await this.assertNoPreparedJournal(store, current.id);
      store.add(structuredClone(journal));
      await transactionDone(transaction);
      return journal;
    } catch (error) {
      abortQuietly(transaction);
      throw versionWriteError(error);
    }
  }

  private journalValue(
    current: ProjectDocumentV3,
    target: ProjectDocumentV3,
    targetVersion: TimetableVersionRecord,
    kind: VersionJournal["kind"],
    now: string,
    baseHash: string,
    targetHash: string
  ): VersionJournal {
    return {
      operation_id: globalThis.crypto.randomUUID(),
      project_id: current.id,
      kind,
      status: "PREPARED",
      version_id: targetVersion.id,
      base_revision: current.document_revision,
      base_hash: baseHash,
      target_revision: target.document_revision,
      target_hash: targetHash,
      target_version: structuredClone(targetVersion),
      created_at: now,
      completed_at: null
    };
  }

  private async makeRecord(
    versionStore: IDBObjectStore,
    document: ProjectDocumentV3,
    sourceApplicationId: string,
    application: VersionApplication,
    provenance: TimetableVersionProvenance,
    now: string,
    sourceOverride?: TimetableVersionSource
  ): Promise<TimetableVersionRecord> {
    const dateKey = shanghaiDateKey(new Date(now));
    const existing = await requestResult<TimetableVersionRecord[]>(
      versionStore.index("project_id").getAll(document.id)
    );
    const daySequence =
      Math.max(
        0,
        ...existing
          .filter((value) => value.date_key === dateKey)
          .map((value) => value.day_sequence)
      ) + 1;
    const requiredLessons = application.completeness === "COMPLETE"
      ? document.entities.timetable_lessons.length
      : requiredLessonsFromUnassigned(
          document.entities.timetable_lessons.length,
          provenance.unassigned_requirements
        );
    return {
      id: globalThis.crypto.randomUUID(),
      project_id: document.id,
      label: `${dateKey}v${daySequence}`,
      date_key: dateKey,
      day_sequence: daySequence,
      source: sourceOverride ?? application.source,
      completeness: application.completeness,
      scheduled_lessons: document.entities.timetable_lessons.length,
      required_lessons: requiredLessons,
      solver_status: application.solver_status,
      optimality_claim: application.solver_status === "OPTIMAL",
      actual_repairs: structuredClone(provenance.actual_repairs),
      unassigned_requirements: structuredClone(provenance.unassigned_requirements),
      blocker_factors: structuredClone(provenance.blocker_factors),
      condition_revision: document.condition_revision,
      condition_fingerprint: document.reference_condition_fingerprint,
      source_application_id: sourceApplicationId,
      created_at: now,
      status: "PREPARED",
      document_snapshot: structuredClone(document)
    };
  }

  private async byApplication(
    applicationId: string
  ): Promise<TimetableVersionRecord | null> {
    const transaction = this.database.transaction(VERSION_STORE, "readonly");
    const value = await requestResult<TimetableVersionRecord | undefined>(
      transaction.objectStore(VERSION_STORE).index("source_application_id").get(
        applicationId
      )
    );
    await transactionDone(transaction);
    return value ? structuredClone(value) : null;
  }

  private async preparedJournals(projectId: string): Promise<VersionJournal[]> {
    const transaction = this.database.transaction(JOURNAL_STORE, "readonly");
    const values = await requestResult<VersionJournal[]>(
      transaction.objectStore(JOURNAL_STORE).index("project_id").getAll(projectId)
    );
    await transactionDone(transaction);
    return values
      .filter((value) => value.status === "PREPARED")
      .sort((left, right) => left.created_at.localeCompare(right.created_at));
  }

  private async assertNoPreparedJournal(
    store: IDBObjectStore,
    projectId: string
  ): Promise<void> {
    const values = await requestResult<VersionJournal[]>(
      store.index("project_id").getAll(projectId)
    );
    if (values.some((value) => value.status === "PREPARED")) {
      throw new TimetableVersionStorageError(
        "VERSION_RECOVERY_REQUIRED",
        "正在恢复上一次课表保存，请刷新后重试"
      );
    }
  }

  private async commitJournal(operationId: string): Promise<void> {
    const transaction = this.database.transaction(
      [VERSION_STORE, HEAD_STORE, JOURNAL_STORE],
      "readwrite"
    );
    try {
      const journals = transaction.objectStore(JOURNAL_STORE);
      const journal = await requestResult<VersionJournal | undefined>(
        journals.get(operationId)
      );
      if (!journal || journal.status === "COMMITTED") {
        await transactionDone(transaction);
        return;
      }
      const now = new Date().toISOString();
      const record = {
        ...journal.target_version,
        status: "COMMITTED" as const
      };
      transaction.objectStore(VERSION_STORE).put(structuredClone(record));
      transaction.objectStore(HEAD_STORE).put({
        project_id: journal.project_id,
        version_id: record.id,
        source_application_id: record.source_application_id,
        document_revision: journal.target_revision,
        document_hash: journal.target_hash,
        updated_at: now
      } satisfies TimetableVersionHead);
      journals.put({
        ...journal,
        status: "COMMITTED",
        completed_at: now
      } satisfies VersionJournal);
      await transactionDone(transaction);
    } catch (error) {
      abortQuietly(transaction);
      throw error;
    }
  }

  private async rollbackJournal(operationId: string): Promise<void> {
    const transaction = this.database.transaction(
      [VERSION_STORE, JOURNAL_STORE],
      "readwrite"
    );
    try {
      const journals = transaction.objectStore(JOURNAL_STORE);
      const journal = await requestResult<VersionJournal | undefined>(
        journals.get(operationId)
      );
      if (journal?.status === "PREPARED") {
        if (journal.kind === "APPLICATION") {
          transaction.objectStore(VERSION_STORE).delete(journal.version_id);
        }
        journals.delete(operationId);
      }
      await transactionDone(transaction);
    } catch (error) {
      abortQuietly(transaction);
      throw new TimetableVersionStorageError(
        "VERSION_RECOVERY_FAILED",
        "课表版本恢复失败，请刷新页面",
        error
      );
    }
  }
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.addEventListener("upgradeneeded", () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(VERSION_STORE)) {
        const versions = database.createObjectStore(VERSION_STORE, { keyPath: "id" });
        versions.createIndex("project_id", "project_id");
        versions.createIndex("source_application_id", "source_application_id", {
          unique: true
        });
        versions.createIndex("project_label", ["project_id", "label"], {
          unique: true
        });
      }
      if (!database.objectStoreNames.contains(HEAD_STORE)) {
        database.createObjectStore(HEAD_STORE, { keyPath: "project_id" });
      }
      if (!database.objectStoreNames.contains(JOURNAL_STORE)) {
        const journals = database.createObjectStore(JOURNAL_STORE, {
          keyPath: "operation_id"
        });
        journals.createIndex("project_id", "project_id");
      }
    });
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("IndexedDB open failed")),
      { once: true }
    );
    request.addEventListener(
      "blocked",
      () => reject(new Error("IndexedDB upgrade blocked")),
      { once: true }
    );
  });
}

async function documentHash(document: ProjectDocumentV3): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(canonicalJson(document)));
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

function canonicalJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalJson);
  if (!isRecord(value)) return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, canonicalJson(value[key])])
  );
}

function shanghaiDateKey(value: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(value);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}${part("month")}${part("day")}`;
}

function requiredLessonsFromUnassigned(
  scheduledLessons: number,
  rows: unknown[]
): number {
  let unassigned = 0;
  for (const value of rows) {
    if (!isRecord(value)) continue;
    const count = value.unassigned_lessons;
    if (typeof count === "number" && Number.isInteger(count) && count > 0) {
      unassigned += count;
    }
  }
  return scheduledLessons + unassigned;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isConstraintError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "ConstraintError";
}

function versionWriteError(error: unknown): TimetableVersionStorageError {
  if (error instanceof TimetableVersionStorageError) return error;
  const quota = error instanceof DOMException && error.name === "QuotaExceededError";
  return new TimetableVersionStorageError(
    quota ? "VERSION_STORAGE_QUOTA" : "VERSION_STORAGE_WRITE_FAILED",
    quota
      ? "浏览器空间不足，未保存新课表，原有课表和历史版本保持不变"
      : "课表版本未能安全保存，原有课表保持不变",
    error
  );
}

function abortQuietly(transaction: IDBTransaction): void {
  try {
    transaction.abort();
  } catch {
    // The transaction may already be inactive after an IndexedDB failure.
  }
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("IndexedDB request failed")),
      { once: true }
    );
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener(
      "abort",
      () => reject(transaction.error ?? new Error("IndexedDB transaction aborted")),
      { once: true }
    );
    transaction.addEventListener(
      "error",
      () => reject(transaction.error ?? new Error("IndexedDB transaction failed")),
      { once: true }
    );
  });
}
